import { useNavigate } from "@solidjs/router";
import { animate } from "motion";
import {
  Accessor,
  createEffect,
  createMemo,
  createSignal,
  onMount,
  untrack,
} from "solid-js";

import { CtaState } from "~/components/brand_l2/denominations_grid";
import {
  HubbleEvent,
  RNREvent,
  rnrEventManager,
  webEventManager,
} from "~/data/events";
import { clientRepo } from "~/server/apis/client_repo";
import { BrandL2RouteData } from "~/server/data/brand_l2_route_data";
import { goBack } from "~/shared_states/modal";
import { showSnackBar } from "~/shared_states/snackbar";
import { captureErrorInSentry } from "~/utils/third_party/sentry";
import { Header, Optional } from "~/types";
import {
  generateDeviceVerificationToken,
  getClientIdFromCookie,
} from "~/utils/common";
import { config } from "~/data/config";
import {
  DiscountDetailsItem,
  ClientMinimalConfigOld,
  SimulatedTransactionDetails,
  PreReviewPopupDetails,
  DiscountDetails,
} from "~/server/types/brand";
import { ClientConfig } from "~/server/types/client";
import {
  getSimulateError,
  getSimulateTransactionDetails,
  getSimulateTransactionDetails2,
} from "~/utils/brandl2";
import { useModal } from "~/components/modal";
import { payOrderStandardCheckout } from "~/data/products";
import { openRazorpayCheckout } from "~/utils/third_party/razorpay";
import { UserProfile } from "~/server/types/user";
import { openPayUCheckout } from "~/utils/third_party/payu";
import { razorpayTDConfig } from "~/utils/razorpay_configs";
import { isOfferAvailed } from "~/core/offer-construct/offer-construct";
import { clientIdConfig } from "~/data/partner_config";
import { useWebStoreContext } from "~/context/webstore_context";
import { ErrorMessage } from "~/data/consts";

export function useBrandPage(brandPageProps: {
  brandKey: string;
  openWarningModal: (props: {
    onContinue: () => void;
    preReviewPopupDetails: PreReviewPopupDetails;
  }) => void;
  openPaymentLoader: (props: {
    amount: number;
    onPaymentComplete: () => void;
    onPaymentFailed: (failureReason: string) => void;
    orderId: string;
    reInit: () => void;
    onClose: () => void;
    brandImageUrl: string;
  }) => void;
  openUserInfoModal: (props: {
    offerConstruct?: any;
    offerConstructTenure: any;
    clientLogoUrl?: string;
    redemptionAmount: any;
    dueAmount: any;
    dueAmount2: any;
    brandDetails?: any;
    selectedAmount: any;
    email: string | null;
    phone: string | null;
    name: string | null;
    onProfileUpdateSuccess: () => void;
    subtitle: string;
  }) => void;
  routeData: Accessor<BrandL2RouteData | undefined>;
}) {
  const { routeData } = brandPageProps;
  const navigate = useNavigate();
  const { storeState, loginToWebstore } = useWebStoreContext();

  const [selectedAmount, setSelectedAmount] = createSignal<Optional<number>>(
    getSimulateTransactionDetails(routeData)?.redemptionAmount ?? null
  );

  const [selectedVariantId, setSelectedVariantId] = createSignal<
    Optional<string>
  >(
    routeData()?.brand?.variantDetailedResponse?.find(
      (variant) => variant.isDefault
    )?.variantId ?? ""
  );

  const [rewardDiscountAmount, setRewardDiscountAmount] = createSignal(0);

  const [initialErrorOnSimulate, setInitialErrorOnSimulate] = createSignal<
    Optional<string>
  >(getSimulateError(routeData) ?? null);

  const [dryRunErrorMessage, setDryRunErrorMessage] =
    createSignal<Optional<string>>(null);

  const [dueAmount, setDueAmount] = createSignal<Optional<number>>(
    getSimulateTransactionDetails(routeData)
      ? getDueAmount(getSimulateTransactionDetails(routeData)!)
      : null
  );
  const [discountDetails, setDiscountDetails] = createSignal<
    Optional<DiscountDetails>
  >(getSimulateTransactionDetails(routeData)?.discountDetails ?? null);
  const [redemptionAmount, setRedemptionAmount] = createSignal<
    Optional<number>
  >(getSimulateTransactionDetails(routeData)?.redemptionAmount ?? null);

  const [dueAmount2, setDueAmount2] = createSignal<Optional<number>>(
    getSimulateTransactionDetails2(routeData)
      ? getDueAmount(getSimulateTransactionDetails2(routeData)!)
      : null
  );
  const [discountDetails2, setDiscountDetails2] = createSignal<
    Optional<DiscountDetails>
  >(getSimulateTransactionDetails2(routeData)?.discountDetails ?? null);
  const [redemptionAmount2, setRedemptionAmount2] = createSignal<
    Optional<number>
  >(getSimulateTransactionDetails2(routeData)?.redemptionAmount ?? null);

  const [offerConstructTenure, setOfferConstructTenure] =
    createSignal<Optional<number>>(null);

  createEffect(() => {
    const subscriptionDetails =
      routeData()?.offerConstruct?.subscriptionRewardDetails;
    if (subscriptionDetails) {
      const defaultTenure =
        subscriptionDetails.find((detail) => detail.isDefault)?.tenure ??
        subscriptionDetails[0]?.tenure;
      setOfferConstructTenure(defaultTenure ?? null);
    }
  });

  const [ctaState, setCtaState] = createSignal<CtaState>("DEFAULT");
  const [isSimulating, setIsSimulating] = createSignal<boolean>(false);

  const [useRewardCoins, setUseRewardCoins] = createSignal<null | boolean>(
    null
  );

  const [hubbleCoinRewards, setHubbleCoinRewards] =
    createSignal<null | DiscountDetailsItem>(
      routeData()?.hubbleCoinRewards ?? null
    );

  const [userProfile, setUserProfile] = createSignal<Optional<UserProfile>>(
    routeData()?.userProfile ?? null
  );

  createEffect(() => {
    setUserProfile(routeData()?.userProfile ?? null);
  });

  const { rnrPortalsJson } = useWebStoreContext();

  const { setIsModalOn } = useModal()!;

  onMount(() => {
    rnrEventManager.setClientId(getClientIdFromCookie() ?? "");
    rnrEventManager.sendEvent(RNREvent.VISIT_BRANDL2_PAGE);
  });

  // To set the selected amount post navigating to the brand page from some other page
  createEffect(() => {
    setHubbleCoinRewards(routeData()?.hubbleCoinRewards ?? null);

    setSelectedVariantId(
      routeData()?.brand?.variantDetailedResponse?.find(
        (variant) => variant.isDefault
      )?.variantId ?? ""
    );

    setDueAmount(
      getSimulateTransactionDetails(routeData)
        ? getDueAmount(getSimulateTransactionDetails(routeData)!)
        : null
    );
    setDiscountDetails(
      getSimulateTransactionDetails(routeData)?.discountDetails ?? null
    );
    setRedemptionAmount(
      getSimulateTransactionDetails(routeData)?.redemptionAmount ?? null
    );

    setDueAmount2(
      getSimulateTransactionDetails2(routeData)
        ? getDueAmount(getSimulateTransactionDetails2(routeData)!)
        : null
    );
    setDiscountDetails2(
      getSimulateTransactionDetails2(routeData)?.discountDetails ?? null
    );
    setRedemptionAmount2(
      getSimulateTransactionDetails2(routeData)?.redemptionAmount ?? null
    );

    setInitialErrorOnSimulate(getSimulateError(routeData));
    setDryRunErrorMessage(null);

    setSelectedAmount(
      getSimulateTransactionDetails(routeData)?.redemptionAmount ?? null
    );
  });

  createEffect(async () => {
    const rewardCoins = useRewardCoins();

    if (rewardCoins !== null) {
      await simulateTransaction(rewardCoins);
    }
  });

  async function simulateTransaction(consumeCoins: boolean) {
    await untrack(async () => {
      setIsSimulating(true);
      setCtaState("DISABLED");

      try {
        if (
          getClientId() === clientIdConfig.tdClientId &&
          routeData()?.offerConstruct?.type === "SUBSCRIPTION_REWARD" &&
          routeData()?.offerConstruct?.subscriptionRewardDetails
        ) {
          const subscriptionDetails =
            routeData()?.offerConstruct?.subscriptionRewardDetails ?? [];
          const simulatePromises = subscriptionDetails.map((detail) =>
            clientRepo.simulate({
              voucherProductId: routeData()?.brand.externalId!,
              amount: redemptionAmount()!,
              variantId: selectedVariantId() ?? "",
              consumeCoins,
              subscriptionDetails: { tenure: detail.tenure },
            })
          );

          const simulateResponses = await Promise.all(simulatePromises);
          const newSimulateTransactionDetails = simulateResponses.map(
            (response) =>
              response.data.filter((data) => data.paymentRoute != "WALLET")[0]
          );

          if (subscriptionDetails.length === 1) {
            setDueAmount(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount(
              newSimulateTransactionDetails[0].redemptionAmount
            );
            setDueAmount2(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails2(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount2(
              newSimulateTransactionDetails[0].redemptionAmount
            );
          } else {
            setDueAmount(getDueAmount(newSimulateTransactionDetails[1]));
            setDiscountDetails(
              newSimulateTransactionDetails[1].discountDetails ?? null
            );
            setRedemptionAmount(
              newSimulateTransactionDetails[1].redemptionAmount
            );
            setDueAmount2(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails2(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount2(
              newSimulateTransactionDetails[0].redemptionAmount
            );
          }

          const hubbleRewardsDetails = simulateResponses.map(
            (response) =>
              response.data
                .filter(({ paymentMode }) => paymentMode === "UPI")
                .map(({ discountDetails }) => {
                  const { totalDiscount, coinDiscount } = discountDetails;
                  return {
                    hubbleCoinRewards: {
                      amount: totalDiscount.amount - coinDiscount.amount,
                      percentage:
                        totalDiscount.percentage - coinDiscount.percentage,
                    },
                  };
                })[0]
          );

          setHubbleCoinRewards(hubbleRewardsDetails[0].hubbleCoinRewards);
        } else {
          const newSimulateResponse = await clientRepo.simulate({
            voucherProductId: routeData()?.brand.externalId!,
            amount: redemptionAmount()!,
            variantId: selectedVariantId() ?? "",
            consumeCoins,
          });

          const newSimulateTransactionDetails = newSimulateResponse.data.filter(
            (data) => data.paymentRoute != "WALLET"
          )[0];

          const newSimulateHubbleRewardsDetails = newSimulateResponse.data
            .filter(({ paymentMode }) => paymentMode === "UPI")
            .map(({ discountDetails }) => {
              const { totalDiscount, coinDiscount } = discountDetails;
              return {
                hubbleCoinRewards: {
                  amount: totalDiscount.amount - coinDiscount.amount,
                  percentage:
                    totalDiscount.percentage - coinDiscount.percentage,
                },
              };
            })[0];

          setDueAmount(getDueAmount(newSimulateTransactionDetails));
          setDiscountDetails(
            newSimulateTransactionDetails.discountDetails ?? null
          );
          setRedemptionAmount(newSimulateTransactionDetails.redemptionAmount);
          setHubbleCoinRewards(
            newSimulateHubbleRewardsDetails?.hubbleCoinRewards
          );
        }
      } catch (e: any) {
        console.log(`got error ${e}`);
        console.log(`got error ${e.message}`);
        setDryRunErrorMessage(e.message);
      } finally {
        setIsSimulating(false);
        setCtaState("DEFAULT");
      }
    });
  }

  createEffect(async () => {
    const newAmount = selectedAmount();
    const rewardCoins = useRewardCoins();

    if (!newAmount) return;
    await untrack(async () => {
      if (newAmount == redemptionAmount()) {
        setDryRunErrorMessage(null);
        return;
      }
      setIsSimulating(true);
      setCtaState("DISABLED");

      try {
        if (
          getClientId() === clientIdConfig.tdClientId &&
          routeData()?.offerConstruct?.type === "SUBSCRIPTION_REWARD" &&
          routeData()?.offerConstruct?.subscriptionRewardDetails
        ) {
          const subscriptionDetails =
            routeData()?.offerConstruct?.subscriptionRewardDetails ?? [];
          const simulatePromises = subscriptionDetails.map((detail) =>
            clientRepo.simulate({
              voucherProductId: routeData()?.brand.externalId!,
              amount: newAmount,
              variantId: selectedVariantId() ?? "",
              consumeCoins: getMode() == "rnr" || rewardCoins === true,
              subscriptionDetails: { tenure: detail.tenure },
            })
          );

          const simulateResponses = await Promise.all(simulatePromises);
          const newSimulateTransactionDetails = simulateResponses.map(
            (response) =>
              response.data.filter((data) => data.paymentRoute != "WALLET")[0]
          );

          if (subscriptionDetails.length === 1) {
            setDueAmount(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount(
              newSimulateTransactionDetails[0].redemptionAmount
            );
            setDueAmount2(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails2(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount2(
              newSimulateTransactionDetails[0].redemptionAmount
            );
          } else {
            setDueAmount(getDueAmount(newSimulateTransactionDetails[1]));
            setDiscountDetails(
              newSimulateTransactionDetails[1].discountDetails ?? null
            );
            setRedemptionAmount(
              newSimulateTransactionDetails[1].redemptionAmount
            );
            setDueAmount2(getDueAmount(newSimulateTransactionDetails[0]));
            setDiscountDetails2(
              newSimulateTransactionDetails[0].discountDetails ?? null
            );
            setRedemptionAmount2(
              newSimulateTransactionDetails[0].redemptionAmount
            );
          }

          const hubbleRewardsDetails = simulateResponses.map(
            (response) =>
              response.data
                .filter(({ paymentMode }) => paymentMode === "UPI")
                .map(({ discountDetails }) => {
                  const { totalDiscount, coinDiscount } = discountDetails;
                  return {
                    hubbleCoinRewards: {
                      amount: totalDiscount.amount - coinDiscount.amount,
                      percentage:
                        totalDiscount.percentage - coinDiscount.percentage,
                    },
                  };
                })[0]
          );

          setHubbleCoinRewards(hubbleRewardsDetails[0].hubbleCoinRewards);
          setRewardDiscountAmount(
            newSimulateTransactionDetails[0].discountDetails?.coinDiscount
              .amount ?? 0
          );
          setDryRunErrorMessage(null);
        } else {
          const newSimulateResponse = await clientRepo.simulate({
            voucherProductId: routeData()?.brand.externalId!,
            amount: newAmount,
            variantId: selectedVariantId() ?? "",
            consumeCoins: getMode() == "rnr" || rewardCoins === true,
          });

          const newSimulateTransactionDetails = newSimulateResponse.data.filter(
            (data) => data.paymentRoute != "WALLET"
          )[0];

          const newSimulateHubbleRewardsDetails = newSimulateResponse.data
            .filter(({ paymentMode }) => paymentMode === "UPI")
            .map(({ discountDetails }) => {
              const { totalDiscount, coinDiscount } = discountDetails;
              return {
                hubbleCoinRewards: {
                  amount: totalDiscount.amount - coinDiscount.amount,
                  percentage:
                    totalDiscount.percentage - coinDiscount.percentage,
                },
              };
            })[0];

          setHubbleCoinRewards(
            newSimulateHubbleRewardsDetails?.hubbleCoinRewards
          );
          setRewardDiscountAmount(
            newSimulateTransactionDetails.discountDetails?.coinDiscount
              .amount ?? 0
          );

          const newDueAmount = getDueAmount(newSimulateTransactionDetails);
          animateDueAmount(dueAmount()!, newDueAmount, () => {});
          setDueAmount(newDueAmount);
          setDiscountDetails(
            newSimulateTransactionDetails.discountDetails ?? null
          );
          setRedemptionAmount(newSimulateTransactionDetails.redemptionAmount);
          setDryRunErrorMessage(null);
        }
      } catch (e: any) {
        console.log(`got error ${e}`);
        console.log(`got error ${e.message}`);
        setDryRunErrorMessage(e.message);
      } finally {
        setIsSimulating(false);
        setCtaState("DEFAULT");
      }
    });
  });

  function getDueAmount(
    simulatedTransactionDetails: SimulatedTransactionDetails
  ) {
    if (simulatedTransactionDetails.paymentSource == "COINS_ONLY") {
      return simulatedTransactionDetails.totalRequiredCoins;
    } else {
      return simulatedTransactionDetails.dueAmount;
    }
  }

  const onClickGetThisCard = async () => {
    // check login and initiate order
    async function checkLoginAndInitiateOrder() {
      if (!storeState.isLoggedIn) {
        await loginToWebstore(onAfterLogin);
      } else {
        await onAfterLogin();
      }
    }

    if (
      userProfile() &&
      userProfile()?.phoneNumber == null &&
      rnrPortalsJson?.()?.showUserDataModal
    ) {
      brandPageProps.openUserInfoModal({
        offerConstruct: routeData()?.offerConstruct,
        offerConstructTenure: offerConstructTenure,
        clientLogoUrl: routeData()?.clientConfig?.clientLogoUrl,
        redemptionAmount: redemptionAmount,
        dueAmount: dueAmount,
        dueAmount2: dueAmount2,
        brandDetails: routeData()?.brand.voucherProductMetadata,
        selectedAmount: selectedAmount,
        email: userProfile()?.email ?? null,
        phone: userProfile()?.phoneNumber ?? null,
        name: fullUserName(),
        onProfileUpdateSuccess: () => {
          checkLoginAndInitiateOrder();
          // update user profile in background
          clientRepo.getUserProfile().then((userProfile) => {
            setUserProfile(userProfile);
          });
        },
        subtitle: getUserDataCollectionModalSubtitle(),
      });
      return;
    }
    await checkLoginAndInitiateOrder();
  };

  function fullUserName() {
    return `${userProfile()?.firstName ?? ""} ${userProfile()?.lastName ?? ""}`.trim();
  }

  function getUserDataCollectionModalSubtitle() {
    if (isClientRaisoni()) {
      return "Share your details to place an order";
    }
    return "Card details will be sent on your whatsapp & email";
  }

  async function onAfterLogin() {
    const preReviewPopupDetails =
      routeData()?.brand.voucherProductMetadata.preReviewPopupDetails;

    if (preReviewPopupDetails != null && getMode() === "hubble") {
      brandPageProps.openWarningModal({
        onContinue: async () => {
          setIsModalOn(false);
          await new Promise((resolve) => setTimeout(resolve, 300));
          await initiateOrder();
        },
        preReviewPopupDetails: preReviewPopupDetails,
      });
    } else {
      await initiateOrder();
    }
  }

  const initiateOrder = async () => {
    try {
      setCtaState("LOADING");

      const amount = selectedAmount();
      if (!amount) {
        throw new Error("Can't initiate order when amount is null");
      }

      let headers: Record<string, any> = {};
      if (storeState.v4HomePage?.user.fraudChecksEnabled) {
        const token = await generateDeviceVerificationToken();
        if (!token) {
          const err = new Error("FPJS token is undefined");
          captureErrorInSentry(err);
          showSnackBar({
            message: ErrorMessage.SECURITY_CHECK_FAILED,
            level: "error",
          });
          throw err;
        }
        headers[Header.FpDeviceToken] = token;
      }

      const initiateRedemptionResponse = await clientRepo.initiateRedemption(
        {
          amount: amount,
          paymentMode: "UPI",
          productId: routeData()?.brand.externalId!,
          ...(routeData()?.brand.amountConditions.denominations != null
            ? {
                denominationDetails: [
                  {
                    denomination: amount,
                    quantity: 1,
                  },
                ],
              }
            : {}),
          variantId: selectedVariantId() ?? "",
          consumeCoins: getMode() == "rnr" || (useRewardCoins() ?? false),
          ...(routeData()?.offerConstruct?.type === "SUBSCRIPTION_REWARD" && {
            subscriptionDetails: { tenure: offerConstructTenure()! },
          }),
        },
        headers
      );

      webEventManager.sendEvent(HubbleEvent.PAYMENT_INITIATED, {
        brandName: routeData()!.brand.name.toLowerCase(),
        amount: amount,
        paymentMode: "UPI",
        consumeCoins: getMode() == "rnr" || (useRewardCoins() ?? false),
        couponCode: "",
        productId: routeData()!.brand.externalId,
      });

      function onPaymentFailed(failureReason: string) {
        goBack(() => {
          webEventManager.sendEvent(HubbleEvent.PAYMENT_FAIL, {
            brandName: routeData()!.brand.name.toLowerCase(),
            amount: selectedAmount(),
            reason: failureReason,
          });
          navigate(
            `/buy-gift-card/${routeData()!.brand.brandKey}/order` +
              `?orderId=${initiateRedemptionResponse.storeOrderDetails.id}` +
              `&paymentFailureReason=${failureReason}`,
            {
              resolve: true,
              state: getNavigationState(),
            }
          );
        });
      }

      function onPaymentComplete() {
        webEventManager.sendEvent(HubbleEvent.PAYMENT_SUCCESS, {
          brandName: routeData()!.brand.name.toLowerCase(),
          amount: selectedAmount(),
        });

        const orderId = encodeURIComponent(
          initiateRedemptionResponse.storeOrderDetails.id
        );
        const brandKey = encodeURIComponent(routeData()!.brand.brandKey);

        navigate(`/buy-gift-card/${brandKey}/order?orderId=${orderId}`, {
          resolve: true,
          state: getNavigationState(),
        });
      }

      function collectPaymentViaCustomCheckout() {
        brandPageProps.openPaymentLoader({
          amount: initiateRedemptionResponse.depositPaymentDetails.amount,
          onPaymentComplete: async () => {
            goBack();
            await new Promise((resolve) => setTimeout(resolve, 300));
            onPaymentComplete();
          },
          onPaymentFailed: onPaymentFailed,
          orderId: initiateRedemptionResponse.depositPaymentDetails.id,
          reInit: initiateOrder,
          onClose: () => {
            goBack();
          },
          brandImageUrl: routeData()!.brand.voucherProductMetadata.logoUrl,
        });
      }

      async function collectPaymentViaRazorpay(props: {
        depositId: string;
        amount: number;
      }) {
        let order;
        if (routeData()?.offerConstruct?.type !== "SUBSCRIPTION_REWARD") {
          order = await payOrderStandardCheckout(props.depositId);
        }
        const profile = await clientRepo.getUserProfile();
        openRazorpayCheckout({
          amount: props.amount,
          email: profile.email ?? "",
          ...(routeData()?.offerConstruct && {
            overrideConfig: razorpayTDConfig.onlyCardConfigForTD,
          }),
          ...(routeData()?.offerConstruct?.type === "SUBSCRIPTION_REWARD" && {
            overrideOptions: {
              ...razorpayTDConfig.options,
              customer_id:
                initiateRedemptionResponse?.depositPaymentDetails
                  .providerMetadata?.customerId ?? "",
            },
          }),
          mode: routeData()?.offerConstruct ? "CARD" : "UPI",
          orderId: order
            ? order.providerOrderId
            : (initiateRedemptionResponse?.depositPaymentDetails
                .providerMetadata?.providerOrderId ?? ""),
          phone: profile.phoneNumber ?? "",
          onSuccess: onPaymentComplete,
          onFailure: onPaymentFailed,
        });
      }

      async function collectPaymentViaPayu(props: {
        depositId: string;
        amount: number;
      }) {
        console.log(`collecting payment via Payu`);

        const order = await payOrderStandardCheckout(props.depositId);

        openPayUCheckout(order.providerCustomData.payU!, "UPI", {
          openedFrom: "webstore",
          orderId: initiateRedemptionResponse.storeOrderDetails.id,
          brandIdOrKey: brandPageProps.brandKey,
        });
      }

      console.log(
        `init redemption response - provider is ${initiateRedemptionResponse.depositPaymentDetails.provider}`
      );

      if (
        initiateRedemptionResponse.depositPaymentDetails.status == "SUCCESS"
      ) {
        onPaymentComplete();
      } else {
        // This was done specifically for Raisoni portal where we didn't have Rzp whitelist
        // the portal url. Can be removed once Rzp is stable.
        if (
          initiateRedemptionResponse.depositPaymentDetails.provider == "PAYU" &&
          getMode() == "rnr"
        ) {
          collectPaymentViaPayu({
            depositId: initiateRedemptionResponse.depositPaymentDetails.id,
            amount: amount,
          });
        }
        // Always use moments for Rzp because collect in custom checkout is failing
        // TODO need to fix this
        else if (
          config.webstore.paymentGateway === "razorpay" ||
          getMode() == "rnr"
        ) {
          collectPaymentViaRazorpay({
            depositId: initiateRedemptionResponse.depositPaymentDetails.id,
            amount: amount,
          });
        } else {
          collectPaymentViaCustomCheckout();
        }
      }
      setCtaState("DEFAULT");
    } catch (error: any) {
      showSnackBar({
        message: error.message,
        level: "error",
      });
      setCtaState("DEFAULT");
    }
  };

  function getNavigationState() {
    return {
      headerLogo:
        getMode() === "rnr"
          ? (routeData()?.clientConfig as ClientMinimalConfigOld)?.clientLogoUrl
          : (routeData()?.clientConfig as ClientConfig)?.clientBrandNameLogoUrl,
      isSDK: getMode() === "sdk",
      isLoggedIn: routeData()?.isLoggedIn,
    };
  }

  type UIConfig = {
    showFooter: boolean;
    showBrandingOnBottom: boolean;
    showWhatsappTip: boolean;
    showCloseButtonOnTopRight: boolean;
    showHeaderActions: boolean;
    showBreadcrumbsNav: boolean;
    showHubbleLogoOnBrandCard: boolean;
    showHubbleTrustMarker: boolean;
    showHeader: boolean;
    showFooterNotes: boolean;
  };

  function getMode() {
    return routeData()?.mode ?? routeData()?.sdkMetaData?.mode ?? "hubble";
  }

  function getUIConfig(): UIConfig {
    let mode = getMode();
    let isSdk = mode == "sdk";
    let isRnr = mode == "rnr";
    let isHubble = mode == "hubble";
    return {
      showFooter: isHubble,
      showBrandingOnBottom: isRnr,
      showWhatsappTip:
        (isHubble || isRnr) &&
        !(getClientId() === clientIdConfig.axisClientId) &&
        !(getClientId() === clientIdConfig.tdClientId) &&
        !(getClientId() === clientIdConfig.redeemHubbleClientId) &&
        !isClientRaisoni(),
      showCloseButtonOnTopRight: isSdk,
      showHeaderActions: isHubble || isRnr,
      showBreadcrumbsNav: isHubble || isRnr,
      showHubbleLogoOnBrandCard: isHubble || isRnr,
      showHubbleTrustMarker: isHubble,
      showHeader: isHubble || isRnr,
      showFooterNotes: isHubble,
    };
  }

  function getMetaTitle(): string {
    const baseDiscountPercent =
      getSimulateTransactionDetails(routeData)?.discountDetails
        ?.hubbleBaseDiscount?.percentage ?? 0;

    if (baseDiscountPercent > 0) {
      if (routeData()?.brand.rewardType != "POSTPAID") {
        return `${routeData()!.brand.voucherProductMetadata.title} | Flat ${baseDiscountPercent}% Off | E-Gift Card | No Promo Code Needed`;
      } else {
        return `${routeData()!.brand.voucherProductMetadata.title} | E-Gift Card | Redeem benefit in Hubble App`;
      }
    } else
      return `${routeData()!.brand.voucherProductMetadata.title} | E-Gift Card | No Promo Code Needed`;
  }

  const getClientId = createMemo(() => {
    const data = routeData();
    if (data?.mode === "rnr") {
      return data?.clientId;
    }
  });

  const isClientRaisoni = createMemo(() => {
    return routeData()?.clientId?.includes(config.clientIdPrefix?.raisoni);
  });

  const offerConstructAvailed = createMemo(() => {
    return (
      routeData()?.offerConstruct &&
      isOfferAvailed(routeData()?.offerConstruct!)
    );
  });

  return {
    routeData,
    getUIConfig,
    getMode,
    isLoggedIn: storeState.isLoggedIn,
    offerConstructAvailed,
    getClientId,
    getMetaTitle,
    onClickGetThisCard,
    // move redemptionControls to denomination grid later
    redemptionControls: {
      selectedAmount,
      setSelectedAmount,
      selectedVariantId,
      setSelectedVariantId,
      isSimulating,
      initialErrorOnSimulate,
      dryRunErrorMessage,
      useRewardCoins,
      setUseRewardCoins,
      ctaState,
      hubbleCoinRewards,
      dueAmount,
      discountDetails,
      discountDetails2,
      redemptionAmount,
      dueAmount2,
      redemptionAmount2,
      offerConstructTenure,
      setOfferConstructTenure,
      rewardDiscountAmount,
    },
  };
}

function animateDueAmount(
  currentDueAmount: number,
  newDueAmount: number,
  onAnimationComplete: () => void
) {
  const start = currentDueAmount;
  const diff = newDueAmount - start;
  const elements = document.querySelectorAll(".anim-due-amount")!;
  animate(
    (progress) => {
      if (elements[0]?.innerHTML && elements[1]?.innerHTML) {
        if (progress == 1) {
          onAnimationComplete();
          elements[0].innerHTML = "" + newDueAmount;
          elements[1].innerHTML = "" + newDueAmount;
        } else {
          const toAdd = progress * diff;
          elements[0].innerHTML = "" + (start + toAdd).toFixed(0);
          elements[1].innerHTML = "" + (start + toAdd).toFixed(0);
        }
      } else if (progress == 1) {
        onAnimationComplete();
      }
    },
    {
      duration: 0.5,
      easing: "linear",
    }
  );
}
